/**
 * TileTopBar.tsx — R10 #7
 *
 * Slim horizontal bar anchored to the top of SidescrollerScene. Replaces the
 * collapsed HandSlotDrawer expand-button which caused layout shift in the main
 * screen PlayerHUD.
 *
 * Shows ONLY the active player's detail: seat color pip, name,
 * 💰 tokens / ⚡ energy / ☠️ pollution / 🏭 facility count.
 * Era + round chip at right.
 *
 * Below the bar: hand drawer (expanded BY DEFAULT during tile phase).
 * Cards shown as 5 landscape thumbnails (150px each).
 *
 * VISIBLE WHEN: turnPhase === "landed"
 * Z-INDEX: 102 — above SidescrollerScene (100), below modals (200+).
 *
 * TEST PATH:
 *   1. Roll dice → player lands on tile → TileTopBar appears at top of scene.
 *   2. Hand drawer shows 5 card slots expanded by default.
 *   3. Click chevron → collapse hand drawer (cards hide, bar stays).
 *   4. Click chevron again → expand hand drawer.
 */

import type React from "react";
import { useEffect, useState } from "react";
import { ERA_NAMES, SEAT_COLORS, getCardById, useGameStore } from "../../store/game-store.js";
import type { CardInHand, SeatIndex } from "../../store/game-store.js";
import styles from "./TileTopBar.module.css";

// ---------------------------------------------------------------------------
// Card thumbnail for the drawer
// ---------------------------------------------------------------------------

function DrawerCardThumb({ card }: { card: CardInHand }): React.ReactElement {
  const openCardDetail = useGameStore((s) => s.openCardDetail);
  const def = getCardById(card.id);

  return (
    <button
      type="button"
      className={styles.cardThumb}
      data-card-type={def?.cardType ?? "Event"}
      onClick={() => openCardDetail(card, true)}
      title={`${def?.cardName ?? card.id} — click to view`}
      aria-label={`View card: ${def?.cardName ?? card.id}`}
      data-testid={`tile-top-bar-card-${card.instanceId}`}
    >
      <div className={styles.thumbType}>{(def?.cardType ?? "?").slice(0, 3).toUpperCase()}</div>
      <div className={styles.thumbName}>{def?.cardName ?? card.id}</div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Drawer slot builder — avoids Array.from index-key lint issue
// ---------------------------------------------------------------------------

const MAX_HAND = 5;

function buildDrawerSlots(hand: CardInHand[]): React.ReactElement[] {
  const slots: React.ReactElement[] = [];
  for (let i = 0; i < MAX_HAND; i++) {
    const card = hand[i];
    if (card !== undefined) {
      slots.push(<DrawerCardThumb key={card.instanceId} card={card} />);
    } else {
      slots.push(
        <div
          key={`empty-${i}`}
          className={styles.emptySlot}
          data-testid="ttb-empty-slot"
          aria-hidden="true"
        >
          —
        </div>,
      );
    }
  }
  return slots;
}

// ---------------------------------------------------------------------------
// TileTopBar — public component
// ---------------------------------------------------------------------------

export function TileTopBar(): React.ReactElement | null {
  const turnPhase = useGameStore((s) => s.turnPhase);
  // R11.2 — gate on board-walk flag so the bar does NOT show during the
  // tile-by-tile move animation. Surfaces only after the token arrives.
  const boardAnimationActive = useGameStore((s) => s.boardAnimationActive);
  const currentSeat = useGameStore((s) => s.currentSeat);
  const tokens = useGameStore((s) => s.tokens);
  const energy = useGameStore((s) => s.energy);
  const pollution = useGameStore((s) => s.pollution);
  const round = useGameStore((s) => s.round);
  const era = useGameStore((s) => s.era);
  const hands = useGameStore((s) => s.hands);
  const builtFacilities = useGameStore((s) => s.builtFacilities);
  const ownedBy = useGameStore((s) => s.ownedBy);

  // Drawer expanded BY DEFAULT (R10 #7 spec: "expand by default")
  // R10 codex MED 1: reset to expanded on every fresh tile entry. Otherwise a
  // collapsed drawer persists across landed phases for the same seat.
  const [drawerOpen, setDrawerOpen] = useState(true);
  const tileIdx = useGameStore((s) => s.playerTiles[s.currentSeat]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: deliberate — reset drawerOpen only on phase/seat/tile change, not user toggles.
  useEffect(() => {
    if (turnPhase === "landed") setDrawerOpen(true);
  }, [turnPhase, currentSeat, tileIdx]);

  // Only render during landed phase AND after board walk has settled.
  if (turnPhase !== "landed") return null;
  if (boardAnimationActive) return null;

  const seatIdx = currentSeat as SeatIndex;
  const seatColor = SEAT_COLORS[seatIdx] ?? "#fff";
  const seatLabel = `P${seatIdx + 1}`;

  const t = tokens[seatIdx] ?? 0;
  const e = energy[seatIdx] ?? 0;
  const p = pollution[seatIdx] ?? 0;

  // Count owned facilities for this seat
  const facilityCount = Object.entries(builtFacilities).filter(
    ([tileStr]) => ownedBy[Number(tileStr)] === seatIdx,
  ).length;

  const eraName = ERA_NAMES[era - 1] ?? `Era ${era}`;
  const hand = hands[seatIdx] ?? [];

  return (
    <div
      className={styles.root}
      data-testid="tile-top-bar"
      data-drawer-open={drawerOpen ? "true" : "false"}
      aria-label={`In-tile HUD: ${seatLabel}`}
    >
      {/* ── Top bar ── */}
      <div className={styles.bar}>
        {/* Seat identity */}
        <span
          className={styles.seatPip}
          style={{ backgroundColor: seatColor }}
          aria-hidden="true"
        />
        <span className={styles.seatLabel} style={{ color: seatColor }} data-testid="ttb-seat">
          {seatLabel}
        </span>

        <span className={styles.divider} aria-hidden="true">
          |
        </span>

        {/* Resources */}
        <span className={styles.resource} aria-label={`${t} tokens`} data-testid="ttb-tokens">
          💰 <span className={styles.val}>{t}</span>
        </span>
        <span className={styles.resource} aria-label={`${e} energy`} data-testid="ttb-energy">
          ⚡ <span className={styles.val}>{e}</span>
        </span>
        <span className={styles.resource} aria-label={`${p} pollution`} data-testid="ttb-pollution">
          ☠️ <span className={styles.val}>{p}</span>
        </span>
        <span
          className={styles.resource}
          aria-label={`${facilityCount} facilities`}
          data-testid="ttb-facilities"
        >
          🏭 <span className={styles.val}>{facilityCount}</span>
        </span>

        {/* Spacer pushes era/round to right */}
        <span className={styles.spacer} />

        {/* Era + Round chip */}
        <span className={styles.eraChip} data-testid="ttb-era-round">
          R{round} · {eraName}
        </span>

        {/* Hand drawer toggle */}
        <button
          type="button"
          className={styles.drawerToggle}
          onClick={() => setDrawerOpen((o) => !o)}
          aria-expanded={drawerOpen}
          aria-label={drawerOpen ? "Collapse hand" : "Expand hand"}
          data-testid="ttb-drawer-toggle"
        >
          {drawerOpen ? "▲ HAND" : "▼ HAND"}
        </button>
      </div>

      {/* ── Hand drawer ── */}
      {drawerOpen && (
        <div className={styles.drawer} data-testid="ttb-hand-drawer">
          {buildDrawerSlots(hand)}
        </div>
      )}
    </div>
  );
}
